import React, { FunctionComponent, Suspense, lazy } from "react";
import styles from "./IconViewer.module.scss";
import IconViewerProps from "./IconViewerProps.interface";

import combineClasses from "helpers/combine-classes";
import isBrowserContext from "helpers/isBrowserContext";
import fetchRemoteIcon from "./helpers/fetchRemoteIcon";
import ButtonArrowRight from "./Icons/ButtonArrowRight";

const Facebook = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Facebook")
);
const Instagram = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Instagram")
);
const LinkedIn = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/LinkedIn")
);
const Twitter = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Twitter")
);
const Tiktok = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Tiktok")
);
const Snapchat = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Snapchat")
);
const YouTube = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/YouTube")
);
const Globe = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Globe")
);
const Fullscreen = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Fullscreen")
);
const Tripadvisor = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Tripadvisor")
);
const Drink = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Drink")
);
const Play = lazy(() => import(/* webpackChunkName: "Icons" */ "./Icons/Play"));
const Pause = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Pause")
);
const Calendar = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Calendar")
);
const Location = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Location")
);
const ChevronUp = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/ChevronUp")
);
const Plus = lazy(() => import(/* webpackChunkName: "Icons" */ "./Icons/Plus"));
const ChevronDown = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/ChevronDown")
);
const ChevronCircleLeft = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/ChevronCircleLeft")
);
const ChevronCircleRight = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/ChevronCircleRight")
);
const BigSmile = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/BigSmile")
);
const MediumSmile = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/MediumSmile")
);
const NoSmile = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/NoSmile")
);
const Frown = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Frown")
);
const Search = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Search")
);
const Close = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Close")
);
const ArrowBottomRight = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/ArrowBottomRight")
);
const SoundOn = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/SoundOn")
);
const SoundOff = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/SoundOff")
);
const StartQuote = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/StartQuote")
);
const EndQuote = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/EndQuote")
);
const Education = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Education")
);
const Energy = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Energy")
);
const Handshake = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Handshaking")
);
const Health = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Health")
);
const PeopleHeart = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/PeopleHeart")
);
const WeCare = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/WeCare")
);

const JulefrokostTextIcon = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/JulefrokostTextIcon")
)

const Keg0 = lazy(() => import(/* webpackChunkName: "Icons" */ "./Icons/Keg0"));
const Keg25 = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Keg25")
);
const Keg50 = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Keg50")
);
const Keg75 = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Keg75")
);
const Keg100 = lazy(
  () => import(/* webpackChunkName: "Icons" */ "./Icons/Keg100")
);

const ICON_TYPES: { [key: string]: any } = {
  // Static
  facebook: Facebook,
  search: Search,
  close: Close,
  instagram: Instagram,
  fullscreen: Fullscreen,
  linkedin: LinkedIn,
  twitter: Twitter,
  youtube: YouTube,
  tiktok: Tiktok,
  snapchat: Snapchat,
  tripadvisor: Tripadvisor,
  globe: Globe,
  plus: Plus,
  play: Play,
  pause: Pause,
  location: Location,
  "chevron-up": ChevronUp,
  "chevron-down": ChevronDown,
  "chevron-circle-left": ChevronCircleLeft,
  "chevron-circle-right": ChevronCircleRight,
  "sound-on": SoundOn,
  "sound-off": SoundOff,
  "start-quote": StartQuote,
  "end-quote": EndQuote,
  wecare: WeCare,
  education: Education,
  energy: Energy,
  handshake: Handshake,
  health: Health,
  peopleheart: PeopleHeart,
  "julefrokosttexticon": JulefrokostTextIcon,

  "big-smile": BigSmile,
  "medium-smile": MediumSmile,
  "no-smile": NoSmile,
  frown: Frown,
  "arrow-bottom-right": ArrowBottomRight,
  "buttonArrowRight": ButtonArrowRight,

  Calendar: Calendar,
  Cocktail: Drink,

  "keg-0": Keg0,
  "keg-25": Keg25,
  "keg-50": Keg50,
  "keg-75": Keg75,
  "keg-100": Keg100,
};

const IconViewer: FunctionComponent<IconViewerProps> = (props): JSX.Element => {
  let Icon = props.name ? ICON_TYPES[props.name] : null;

  if (props.url) {
    Icon = lazy(() => fetchRemoteIcon(props.url ? props.url.toString() : ""));
  }

  return (
    <div className={combineClasses(styles.wrapper, props.className)}>
      {isBrowserContext() ? (
        <Suspense fallback={null}>
          {Icon ? <Icon className={styles.icon} /> : null}
        </Suspense>
      ) : null}

      <svg
        viewBox={`0 0 ${props.aspectRatioX ?? 1} ${props.aspectRatioY ?? 1}`}
        className={styles.ratio}
        preserveAspectRatio="xMidYMid"
        xmlSpace="preserve"
        version="1.1"
      ></svg>
    </div>
  );
};

export default IconViewer;
